/// <amd-module name="Repos/PullRequestActions/RestClient/Git" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   tfs\web\extensions\code\vss-code-web\repos\views\pr-details-actions\genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as Git from "Repos/Common/Contracts/Git";

export interface IGitRestClient extends IVssRestClient {
    /**
     * Find the closest common commit (the merge base) between base and target commits, and get the diff between either the base and target commits or common and target commits.
     * 
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param diffCommonCommit - If true, diff between common and target commits. If false, diff between base and target commits.
     * @param top - Maximum number of changes to return. Defaults to 100.
     * @param skip - Number of changes to skip
     * @param baseVersionDescriptor - Descriptor for base commit.
     * @param targetVersionDescriptor - Descriptor for target commit.
     */
    getCommitDiffs(repositoryId: string, project?: string, diffCommonCommit?: boolean, top?: number, skip?: number, baseVersionDescriptor?: Git.GitBaseVersionDescriptor, targetVersionDescriptor?: Git.GitTargetVersionDescriptor): Promise<Git.GitCommitDiffs>;
    /**
     * Post for retrieving a creating a batch out of a set of items in a repo / project given a list of paths or a long path
     * 
     * @param requestData - Request data attributes: ItemDescriptors, IncludeContentMetadata, LatestProcessedChange, IncludeLinks. ItemDescriptors: Collection of items to fetch, including path, version, and recursion level. IncludeContentMetadata: Whether to include metadata for all items LatestProcessedChange: Whether to include shallow ref to commit that last changed each item. IncludeLinks: Whether to include the _links field on the shallow references.
     * @param repositoryId - The name or ID of the repository
     * @param project - Project ID or project name
     */
    getItemsBatch(requestData: Git.GitItemRequestData, repositoryId: string, project?: string): Promise<Git.GitItem[][]>;
    /**
     * Retrieve all conflicts for a pull request
     * 
     * @param repositoryId - The repository of the Pull Request.
     * @param pullRequestId - The pull request ID.
     * @param project - Project ID or project name
     * @param skip - Conflicts to skip.
     * @param top - Conflicts to return after skip.
     * @param includeObsolete - Includes obsolete conflicts.
     * @param excludeResolved - Excludes conflicts already resolved.
     * @param onlyResolved - Returns only the conflicts that are resolved.
     */
    getPullRequestConflicts(repositoryId: string, pullRequestId: number, project?: string, skip?: number, top?: number, includeObsolete?: boolean, excludeResolved?: boolean, onlyResolved?: boolean): Promise<Git.GitConflict[]>;
    /**
     * Sends an e-mail notification about a specific pull request to a set of recipients
     * 
     * @param userMessage - 
     * @param repositoryId - ID of the git repository.
     * @param pullRequestId - ID of the pull request.
     * @param project - Project ID or project name
     */
    sharePullRequest(userMessage: Git.ShareNotificationContext, repositoryId: string, pullRequestId: number, project?: string): Promise<void>;
    /**
     * Push changes to the repository.
     * 
     * @param push - 
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     */
    createPush(push: Git.GitPush, repositoryId: string, project?: string): Promise<Git.GitPush>;
}

class GitRestClient extends RestClientBase implements IGitRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Find the closest common commit (the merge base) between base and target commits, and get the diff between either the base and target commits or common and target commits.
     * 
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     * @param diffCommonCommit - If true, diff between common and target commits. If false, diff between base and target commits.
     * @param top - Maximum number of changes to return. Defaults to 100.
     * @param skip - Number of changes to skip
     * @param baseVersionDescriptor - Descriptor for base commit.
     * @param targetVersionDescriptor - Descriptor for target commit.
     */
    public async getCommitDiffs(
        repositoryId: string,
        project?: string,
        diffCommonCommit?: boolean,
        top?: number,
        skip?: number,
        baseVersionDescriptor?: Git.GitBaseVersionDescriptor,
        targetVersionDescriptor?: Git.GitTargetVersionDescriptor
        ): Promise<Git.GitCommitDiffs> {

        const queryValues: any = {
            diffCommonCommit: diffCommonCommit,
            '$top': top,
            '$skip': skip,
        };
        if (baseVersionDescriptor) {
            queryValues.baseVersionType = baseVersionDescriptor.versionType;
            queryValues.baseVersion = baseVersionDescriptor.version;
            queryValues.baseVersionOptions = baseVersionDescriptor.versionOptions;
        }
        if (targetVersionDescriptor) {
            queryValues.targetVersionType = targetVersionDescriptor.versionType;
            queryValues.targetVersion = targetVersionDescriptor.version;
            queryValues.targetVersionOptions = targetVersionDescriptor.versionOptions;
        }

        return this.beginRequest<Git.GitCommitDiffs>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/diffs/commits",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            queryParams: queryValues
        });
    }

    /**
     * Post for retrieving a creating a batch out of a set of items in a repo / project given a list of paths or a long path
     * 
     * @param requestData - Request data attributes: ItemDescriptors, IncludeContentMetadata, LatestProcessedChange, IncludeLinks. ItemDescriptors: Collection of items to fetch, including path, version, and recursion level. IncludeContentMetadata: Whether to include metadata for all items LatestProcessedChange: Whether to include shallow ref to commit that last changed each item. IncludeLinks: Whether to include the _links field on the shallow references.
     * @param repositoryId - The name or ID of the repository
     * @param project - Project ID or project name
     */
    public async getItemsBatch(
        requestData: Git.GitItemRequestData,
        repositoryId: string,
        project?: string
        ): Promise<Git.GitItem[][]> {

        return this.beginRequest<Git.GitItem[][]>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/ItemsBatch",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            body: requestData
        });
    }

    /**
     * Retrieve all conflicts for a pull request
     * 
     * @param repositoryId - The repository of the Pull Request.
     * @param pullRequestId - The pull request ID.
     * @param project - Project ID or project name
     * @param skip - Conflicts to skip.
     * @param top - Conflicts to return after skip.
     * @param includeObsolete - Includes obsolete conflicts.
     * @param excludeResolved - Excludes conflicts already resolved.
     * @param onlyResolved - Returns only the conflicts that are resolved.
     */
    public async getPullRequestConflicts(
        repositoryId: string,
        pullRequestId: number,
        project?: string,
        skip?: number,
        top?: number,
        includeObsolete?: boolean,
        excludeResolved?: boolean,
        onlyResolved?: boolean
        ): Promise<Git.GitConflict[]> {

        const queryValues: any = {
            '$skip': skip,
            '$top': top,
            includeObsolete: includeObsolete,
            excludeResolved: excludeResolved,
            onlyResolved: onlyResolved
        };

        return this.beginRequest<Git.GitConflict[]>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/conflicts/{conflictId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId
            },
            queryParams: queryValues
        });
    }

    /**
     * Sends an e-mail notification about a specific pull request to a set of recipients
     * 
     * @param userMessage - 
     * @param repositoryId - ID of the git repository.
     * @param pullRequestId - ID of the pull request.
     * @param project - Project ID or project name
     */
    public async sharePullRequest(
        userMessage: Git.ShareNotificationContext,
        repositoryId: string,
        pullRequestId: number,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pullRequests/{pullRequestId}/share",
            routeValues: {
                project: project,
                repositoryId: repositoryId,
                pullRequestId: pullRequestId
            },
            body: userMessage
        });
    }

    /**
     * Push changes to the repository.
     * 
     * @param push - 
     * @param repositoryId - The name or ID of the repository.
     * @param project - Project ID or project name
     */
    public async createPush(
        push: Git.GitPush,
        repositoryId: string,
        project?: string
        ): Promise<Git.GitPush> {

        return this.beginRequest<Git.GitPush>({
            apiVersion: "5.0-preview.2",
            method: "POST",
            routeTemplate: "{project}/_apis/git/repositories/{repositoryId}/pushes/{pushId}",
            routeValues: {
                project: project,
                repositoryId: repositoryId
            },
            body: push
        });
    }

}

export const GitClientName = "Repos.PRActions.Git.IGitRestClient";

export function getGitClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IGitRestClient {
    return pageContext.getRestClient<IGitRestClient>(GitClientName, options);
}

RestClients.add(GitClientName, { 
    factory: GitRestClient,
    options: {
        resourceAreaId: "4e080c62-fa21-4fbc-8fef-2a10a2b38049",
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});
